import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import { ArrowRight, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Executive Launch Readiness Dashboard.
 *
 * The pre-launch go/no-go screen for Display America. Aggregates operational
 * health across 9 sections into a single readiness score. Backed by
 * GET /api/admin/launch-readiness. Card-based, executive tone, minimal.
 */
export default function LaunchReadinessPage() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/launch-readiness");
      setData(data);
    } catch { toast.error("Could not load readiness data."); }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <WorkspaceShell
      title="Launch Readiness"
      subtitle="Executive go/no-go review before opening registration."
      dataTestId="launch-readiness-title"
      tabs={[{
        value: "overview",
        label: "Overview",
        render: () => (
          <div className="max-w-6xl mx-auto" data-testid="launch-readiness">
            {/* Hero score card */}
            <ScoreCard data={data} onRefresh={load} refreshing={refreshing} />

            {/* Sections grid */}
            <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="lr-sections">
              {data?.sections.map((s) => <SectionCard key={s.key} section={s} />)}
            </div>

            {data && (
              <div className="mt-10 text-center text-[11px] uppercase tracking-[0.22em] text-zinc-400">
                Generated {new Date(data.generated_at).toLocaleString()}
              </div>
            )}
          </div>
        ),
      }]}
    />
  );
}

function ScoreCard({ data, onRefresh, refreshing }) {
  if (!data) {
    return (
      <div className="bg-white border border-zinc-200 p-8 md:p-12 rounded-sm text-center">
        <div className="serif text-5xl text-zinc-300">—</div>
        <div className="mt-3 text-sm text-zinc-500">Loading readiness data…</div>
      </div>
    );
  }
  const tone =
    data.overall_status === "ready" ? "text-emerald-600"
    : data.overall_status === "attention" ? "text-amber-600"
    : "text-red-600";
  const accentBar =
    data.overall_status === "ready" ? "bg-emerald-500"
    : data.overall_status === "attention" ? "bg-amber-500"
    : "bg-red-500";

  return (
    <div className="relative bg-white border border-zinc-200 p-8 md:p-12 rounded-sm overflow-hidden" data-testid="lr-score-card">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${accentBar}`} />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="overline mb-3"><span className="accent-rule" />Launch Readiness</div>
          <div className="flex items-baseline gap-3">
            <span className={`serif text-7xl md:text-8xl tabular-nums ${tone}`} data-testid="lr-score">{data.score}<span className="text-3xl">%</span></span>
          </div>
          <div className={`mt-3 text-lg ${tone}`} data-testid="lr-headline">{data.headline}</div>
        </div>

        <div className="grid grid-cols-3 gap-6 text-right md:text-left">
          <StatusTally label="Ready" count={data.sections.filter((s) => s.status === "ready").length} tone="emerald" />
          <StatusTally label="Attention" count={data.sections.filter((s) => s.status === "attention").length} tone="amber" />
          <StatusTally label="Critical" count={data.critical_count} tone="red" />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={onRefresh} disabled={refreshing}
          className="btn-outline-black h-10 px-4 text-xs disabled:opacity-40" data-testid="lr-refresh">
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
        </button>
        <Link to="/admin" className="btn-outline-black h-10 px-4 text-xs" data-testid="lr-back-admin">
          Workspace Home <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}

function StatusTally({ label, count, tone }) {
  const toneMap = {
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    red: "text-red-600",
  };
  return (
    <div>
      <div className={`serif text-3xl tabular-nums ${toneMap[tone]}`}>{count}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function SectionCard({ section }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 md:p-7 rounded-sm" data-testid={`lr-section-${section.key}`}>
      <div className="flex items-baseline justify-between mb-5 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <StatusDot status={section.status} size={10} />
          <div className="serif text-2xl truncate">{section.title}</div>
        </div>
        <StatusPill status={section.status} />
      </div>

      <ul className="grid gap-2.5">
        {section.items.map((it, i) => (
          <li key={i} className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-100 last:border-0" data-testid={`lr-item-${section.key}-${i}`}>
            <div className="flex items-start gap-2.5 min-w-0 flex-1">
              <StatusDot status={it.status} size={6} className="mt-2" />
              <span className="text-sm text-zinc-800 truncate">{it.label}</span>
            </div>
            <span className="text-sm text-zinc-500 text-right shrink-0">{it.value}</span>
          </li>
        ))}
      </ul>

      {(section.critical?.length > 0 || section.attention_list?.length > 0) && (
        <div className="mt-5 pt-4 border-t border-zinc-100 grid gap-2" data-testid={`lr-details-${section.key}`}>
          {(section.critical || []).map((c, i) => (
            <div key={`c-${i}`} className="flex items-start gap-2 text-xs" data-testid={`lr-critical-${section.key}-${i}`}>
              <XCircle size={12} className="text-red-600 mt-0.5 shrink-0" />
              <span className="text-red-700 flex-1">
                <span className="text-[10px] uppercase tracking-[0.18em] mr-1">Blocked</span>
                {c.deliverable}
                {c.due_date && <span className="text-red-500 ml-2">· due {c.due_date}</span>}
              </span>
            </div>
          ))}
          {(section.attention_list || []).map((a, i) => (
            <div key={`a-${i}`} className="flex items-start gap-2 text-xs" data-testid={`lr-attention-${section.key}-${i}`}>
              <AlertTriangle size={12} className="text-amber-600 mt-0.5 shrink-0" />
              <span className="text-amber-800 flex-1">
                <span className="text-[10px] uppercase tracking-[0.18em] mr-1">{a.overdue ? "Overdue" : "At Risk"}</span>
                {a.deliverable}
                {a.due_date && <span className="text-amber-600 ml-2">· due {a.due_date}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {section.quick_action && (
        <div className="mt-5 pt-4 border-t border-zinc-100">
          <Link to={section.quick_action.href}
            className="text-xs uppercase tracking-[0.18em] text-black hover:text-[#A39171] inline-flex items-center gap-2"
            data-testid={`lr-quick-${section.key}`}>
            {section.quick_action.label} <ArrowRight size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status, size = 8, className = "" }) {
  const cls =
    status === "ready" ? "bg-emerald-500"
    : status === "attention" ? "bg-amber-500"
    : "bg-red-500";
  return <span className={`inline-block rounded-full ${cls} ${className}`} style={{ width: size, height: size }} aria-hidden="true" />;
}

function StatusPill({ status }) {
  const map = {
    ready:     { Icon: CheckCircle2, label: "Ready",             cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    attention: { Icon: AlertTriangle, label: "Attention Needed", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    not_ready: { Icon: XCircle,       label: "Not Ready",        cls: "text-red-700 bg-red-50 border-red-200" },
  };
  const { Icon, label, cls } = map[status] || map.attention;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 border ${cls}`}>
      <Icon size={11} strokeWidth={2.2} /> {label}
    </span>
  );
}
